import { useEffect, useMemo, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller, FieldErrors } from "react-hook-form";
import img from "../../../assets/images/InceptionFinicallyLogo.png";
import styles from "./index.module.css";
import CustomSwitch from "../../CustomSwitch";
import { proformaFormSchema } from "../../../yup";
import Select from "react-select";
import { config } from "../../../configs/index";
import { useNavigate } from "react-router-dom";
import { resetProformaUser } from "../../../store/reducers/proformaReducer";
import { useDispatch, useSelector } from "react-redux";
import useProformaValues from "../../../hooks/useSetProformaValues";
import Loader from "../../Loader";
import ProformaResponse from "../../ProformaResponse";
import { RootState } from "../../../store/store";
import {
  useGetDefaultProformaValuesQuery,
  useGetBaselineModalQuery,
  useGetContactGroupQuery,
  useSendProformaEmailMutation,
  useGetStatesInfoQuery,
  useGetAssumptionInputDataQuery,
  useCreateAmpVantageProformaMutation,
  useCreateMainProformaMutation,
  useExistingProformaMutation,
  proformaApi,
  useGenerateProformaMutation,
  useLazyGenerateProformaPollingQuery,
  useGenerateAmpVantageProformaMutation,
} from "../../../services/proformaApi";
import { FaRegQuestionCircle } from "react-icons/fa";
import GlobalModal from "../../../helpers/GlobalModal";
import ExistingProforma from "../ExistingProforma";
import ProformaLocated from "../ProformaLocated";
import WithoutValidEmailModal from "../WithoutValidEmailModal";
import { tracker } from "../../../services/mixpanel";
import { showNotification } from "../../../services/showNotification";
import clsx from "clsx";

const basePartnerMarkupOptions = [
  { label: "0%", value: "0%" },
  { label: "0.5%", value: "0.5%" },
  { label: "1%", value: "1%" },
  { label: "1.5%", value: "1.5%" },
  { label: "2%", value: "2%" },
  { label: "2.5%", value: "2.5%" },
  { label: "3%", value: "3%" },
  { label: "3.5%", value: "3.5%" },
  { label: "4%", value: "4%" },
  { label: "4.5%", value: "4.5%" },
  { label: "5%", value: "5%" },
  { label: "5.5%", value: "5.5%" },
  { label: "6%", value: "6%" },
  { label: "6.5%", value: "6.5%" },
  { label: "7%", value: "7%" },
  { label: "7.5%", value: "7.5%" },
  { label: "8%", value: "8%" },
  { label: "8.5%", value: "8.5%" },
  { label: "9%", value: "9%" },
  { label: "9.5%", value: "9.5%" },
  { label: "10%", value: "10%" },
  { label: "10.5%", value: "10.5%" },
  { label: "11%", value: "11%" },
  { label: "11.5%", value: "11.5%" },
  { label: "12%", value: "12%" },
  { label: "12.5%", value: "12.5%" },
  { label: "13%", value: "13%" },
  { label: "13.5%", value: "13.5%" },
  { label: "14%", value: "14%" },
  { label: "14.5%", value: "14.5%" },
  { label: "15%", value: "15%" },
];

const tillTwentyPartnerMarkup = [
  { label: "15.5%", value: "15.5%" },
  { label: "16%", value: "16%" },
  { label: "16.5%", value: "16.5%" },
  { label: "17%", value: "17%" },
  { label: "17.5%", value: "17.5%" },
  { label: "18%", value: "18%" },
  { label: "18.5%", value: "18.5%" },
  { label: "19%", value: "19%" },
  { label: "19.5%", value: "19.5%" },
  { label: "20%", value: "20%" },
];

const tillTwentyThreePartnerMarkup = [
  { label: "20.5%", value: "20.5%" },
  { label: "21%", value: "21%" },
  { label: "21.5%", value: "21.5%" },
  { label: "22%", value: "22%" },
  { label: "22.5%", value: "22.5%" },
  { label: "23%", value: "23%" },
];

interface IToggleSwitch {
  isAmpVantageActive: boolean;
  isUseAvailableTaxCredit: boolean;
  isUseAdjustedGrossIncome: boolean;
  isLossLimitation: boolean;
  isFederalRevenueSpread: boolean;
  isFederalBonusDepreciation: boolean;
  isFullProforma: boolean;
  [field: string]: boolean;
  isNewClient: false;
}
interface State {
  _id: string;
  state: string;
}

interface StateOption {
  label: string;
  value: string;
}

const ProformaFormV2 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasStarted = useRef(false);
  const { setProformaValues } = useProformaValues();
  const [toggleSwitch, setToggleSwitch] = useState<IToggleSwitch>({
    isAmpVantageActive: false,
    isUseAvailableTaxCredit: false,
    isUseAdjustedGrossIncome: false,
    isLossLimitation: true,
    isFederalRevenueSpread: false,
    isFederalBonusDepreciation: false,
    isNewClient: false,
    isFullProforma: true,
  });
  const profromaUserDetails = useSelector(
    (state: RootState) => state.proformaUser
  );
  const isAdmin = profromaUserDetails?.isAdmin;
  const [isExternal, setIsExternal] = useState(
    profromaUserDetails?.designation === "External"
  );
  const [stateTaxRatePercent, setStateTaxRatePercent] = useState<number>();
  const isReallyExternal = profromaUserDetails?.designation === "External";
  const showFederalRevenueYearSelect =
    !isExternal || !toggleSwitch.isFederalRevenueSpread;

  const [selectedContactName, setSelectedContactName] = useState(
    profromaUserDetails?.contactGroupName || "Inception Financial"
  );

  const {
    watch,
    register,
    clearErrors,
    handleSubmit,
    reset,
    setValue,
    control,
    getValues,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(proformaFormSchema),
    mode: "onChange",
    context: {
      isEquity:
        !toggleSwitch.isUseAvailableTaxCredit &&
        !toggleSwitch.isUseAdjustedGrossIncome,
      isGross: toggleSwitch.isUseAdjustedGrossIncome,
      isITC: !toggleSwitch.isUseAvailableTaxCredit,
      isExternal: isExternal,
      contactGroupName:
        profromaUserDetails?.contactGroupName ?? selectedContactName,
    },
    defaultValues: {
      clientEmailAddress: "",
      isClientEmailDisabled: false,
      year2DeferralPossible: "",
      federalRevenueRecognitionYear: 1,
      stateRevenueRecognitionYear: 1,
    },
  });
  const { data: states, isLoading: loading } = useGetStatesInfoQuery();
  const { data: defaultValues, isLoading: defaultValuesLoader } =
    useGetDefaultProformaValuesQuery();
  const { data: contactGroupData, isLoading: contactGroupLoader } =
    useGetContactGroupQuery();

  const [baseLineName, setBaseLineName] = useState<string>("");

  const {
    data: baselineModalData,
    isLoading: baselineModalLoader,
    refetch,
  } = useGetBaselineModalQuery(baseLineName, {
    skip: !baseLineName, // don’t call if undefined
  });
  const [sendProformaEmail] = useSendProformaEmailMutation();
  const [createAmpVantageProforma] = useCreateAmpVantageProformaMutation();
  const [createMainProforma] = useCreateMainProformaMutation();
  const { data: assumptionInput, isLoading: assumptionInputLoader } =
    useGetAssumptionInputDataQuery(config.REACT_APP_MAIN_SPREAD_SHEET_ID || "");
  const [generateProforma] = useGenerateProformaMutation();
  const [generateAmpVantageProforma] = useGenerateAmpVantageProformaMutation();
  const [driveFolderId, setDriveFolderId] =
    useState<IGenerateProformaRes | null>(null);

  const [isExistingProformaModalOpen, setIsExistingProformaModalOpen] =
    useState(false);
  const [isClientEmailDisabled, setIsClientEmailDisabled] = useState(false);
  const [proformaUrlId, setProformaUrlId] = useState("");
  const [proformaMatchStatus, setProformaMatchStatus] = useState<
    boolean | null
  >(null);
  const [isProformaLocatedModalOpen, setIsProformaLocatedModalOpen] =
    useState(false);
  const [partnerMarkupOptions, setPartnerMarkupOptions] = useState(
    basePartnerMarkupOptions
  );
  const [isIndividualIdZoho, setIsIndividualIdZoho] = useState(false);
  const inceptionFolderId = useMemo(() => {
    if (!contactGroupData?.success || !Array.isArray(contactGroupData.data)) {
      return null;
    }

    const match = contactGroupData.data.find(
      (group) => group.contactGroupName == "Inception Financial"
    );

    return match?.folderId || null;
  }, [contactGroupData]);

  const [proformaId, setProformaId] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [withoutEmailModal, setWithoutEmailModal] = useState(false);
  const [proformaLocatedMessage, setProformaLocatedMessage] = useState("");
  const [existingProforma, { isLoading: isExistingProformaLoading }] =
    useExistingProformaMutation();
  const [isAlreadyFinalized, setIsAlreadyFinalized] = useState(false);
  const [isContactGroupDisabled, setIsContactGroupDisabled] = useState(false);
  const handleProformaCheckResult = (
    isMatch: ExistingProformaResponse,
    enteredProformaId: string
  ) => {
    setIsExistingProformaModalOpen(false);
    setIsAlreadyFinalized(false);
    setProformaMatchStatus(isMatch?.output === "true" ? true : false);
    if (isMatch?.proformaList?.Pro_Forma_ID == enteredProformaId) {
      setIsAlreadyFinalized(true);
      setProformaLocatedMessage(
        `You already have proforma with Proforma ID: ${
          isMatch.proformaList.Pro_Forma_ID
        } as Finalized Proforma for ${new Date().getFullYear()}.`
      );
    } else if (!isMatch?.proformaList?.Pro_Forma_ID) {
      setProformaLocatedMessage("");
    } else if (
      isMatch?.proformaList?.Pro_Forma_ID != enteredProformaId &&
      isMatch?.proformaList?.Pro_Forma_ID
    ) {
      setProformaLocatedMessage(
        `You currently have proforma with Proforma ID: ${
          isMatch.proformaList.Pro_Forma_ID
        } as Finalized Proforma for ${new Date().getFullYear()}.`
      );
    }
    if (isMatch.output === "false") {
      setProformaLocatedMessage(isMatch.userMessage[0] || "");
    }
    setIsProformaLocatedModalOpen(true);
  };
  const [stateOptions, setStateOptions] = useState<StateOption[]>();
  const [contactGroupOptions, setContactGroupOptions] =
    useState<StateOption[]>();
  const [months] = useState([
    ...Array.from({ length: 12 }, (_, i) => `${i + 1}`),
  ]);
  const [errMsg, setErrMsg] = useState("");
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [dataToPrefill, setDataToPrefill] = useState<string[][]>([]);
  const [wasReferred, setWasReferred] = useState(true);
  const getStates = () => {
    const data = states?.data || [];
    const stateOption: StateOption[] = data.map((item: State) => {
      return {
        label: item.state,
        value: item._id,
      };
    });
    setStateOptions(stateOption);
  };
  const handleClientEmailClear = () => {
    setIsClientEmailDisabled(false);
    reset({
      isClientEmailDisabled: false,
      clientEmailAddress: "",
    });
  };

  const updatePartnerMarkupOptions = (
    allowedTwentyPercent: boolean,
    isGreaterThanFifteen: boolean,
    isGreaterThanTwenty: boolean
  ) => {
    let updatedOptions = [...basePartnerMarkupOptions];

    if (isGreaterThanTwenty) {
      updatedOptions = [
        ...updatedOptions,
        ...tillTwentyPartnerMarkup,
        ...tillTwentyThreePartnerMarkup,
      ];
    } else if (isGreaterThanFifteen || allowedTwentyPercent) {
      updatedOptions = [...updatedOptions, ...tillTwentyPartnerMarkup];
    }

    setPartnerMarkupOptions(updatedOptions);
  };

  const handleClick = (field: string) => {
    let newObj = { ...toggleSwitch, [field]: !toggleSwitch[field] };
    if (field === "isAmpVantageActive") {
      newObj.isLossLimitation = !newObj.isAmpVantageActive;
      newObj.isFederalRevenueSpread = newObj.isAmpVantageActive;
      if (newObj.isAmpVantageActive === true) {
        setValue(
          "ITCPercentage",
          `${baselineModalData?.data?.ampVantageItcPercent!}%`
        );
      } else {
        setValue("ITCPercentage", `${baselineModalData?.data?.itcPercent!}%`);
      }
    } else if (field == "isFederalBonusDepreciation") {
      setValue(
        "federalBonusDepreciationPercentage",
        `${baselineModalData?.data?.federalBonusDepreciation!}%`
      );
    } else if (
      field === "isUseAdjustedGrossIncome" &&
      toggleSwitch.isUseAdjustedGrossIncome === true
    ) {
      setValue("adjustedGrossIncome", "", { shouldValidate: true });
      clearErrors("adjustedGrossIncome");
    }
    setToggleSwitch(newObj);
  };

  const latestFormValues = useRef<ProformaForm | null>(null);

  const handleEmailDisable = async (values: ProformaForm) => {
    const clientEmail = getValues("clientEmailAddress");

    if (isClientEmailDisabled && !clientEmail) {
      latestFormValues.current = values;
      setWithoutEmailModal(true);
      return;
    }
    setContactEmail(clientEmail || "");
    let urlId;
    let individualContactId;
    if (!isClientEmailDisabled && clientEmail) {
      try {
        const response = await existingProforma({
          contactEmail: clientEmail,
        }).unwrap();
        if (response.data.output === "false") {
          latestFormValues.current = values;
          setWithoutEmailModal(true);
          setProformaUrlId("");
          return;
        }
        urlId = response?.data?.proformaUrlId;
        individualContactId = response?.data?.individualFolderId;
        if (individualContactId) {
          setIsIndividualIdZoho(true && isReallyExternal);
        }
        setProformaUrlId(urlId);
        setContactEmail(clientEmail);
      } catch (error) {
        console.error("Failed to verify existing proforma", error);
        return;
      }
    }

    onSubmit(values, urlId, individualContactId);
    // onSubmit(values);
  };

  const onError = () => {
    tracker.proforma.formError(profromaUserDetails?.name);
  };

  const onSubmit = async (
    values: ProformaForm,
    urlId?: string,
    individualContactId?: string
  ) => {
    if (!toggleSwitch.isUseAdjustedGrossIncome) {
      setValue("adjustedGrossIncome", "");
    }
    if (!toggleSwitch.isUseAvailableTaxCredit) {
      setValue("ITCAmount", "");
    }
    if (
      toggleSwitch.isUseAvailableTaxCredit ||
      toggleSwitch.isUseAdjustedGrossIncome
    ) {
      setValue("equityContribution", "$100000");
    }
    if (!toggleSwitch.isFederalRevenueSpread) {
      setValue("monthlyPlacedInService", "");
    }

    const stateName = watch("stateSelector");

    const targetState =
      states?.data?.filter((state) => state?.state === stateName?.label) || [];

    if (stateName?.value && isExternal && targetState.length) {
      setBaseLineValuesWithoutPartnerMarkup(targetState);
    }

    let submissionValues = {
      ...values,
    };
    let referralMarkup = 0,
      partnerMarkup = "0%";

    // Handle external user case
    if (
      isExternal &&
      submissionValues.inceptionDevFee &&
      values.partnerMarkup
    ) {
      partnerMarkup = values.partnerMarkup;
      // const newInceptionDevFee =
      //   parseFloat(getValues("inceptionDevFee").replace(/%/g, "")) +
      //   parseFloat(values.partnerMarkup.replace(/%/g, ""));
      // submissionValues.inceptionDevFee = `${newInceptionDevFee.toString()}%`;
    }
    // Handle Inception Financial referral case
    else if (
      !isExternal &&
      selectedContactName === "Inception Financial" &&
      wasReferred &&
      submissionValues.inceptionDevFee
    ) {
      // const currentInceptionDevFee = parseFloat(
      //   submissionValues.inceptionDevFee.replace(/%/g, "")
      // );
      referralMarkup = baselineModalData?.data?.isReferralMarkup || 2;
      // const totalDevFee = currentInceptionDevFee + referralMarkup;
      // submissionValues.inceptionDevFee = `${totalDevFee.toString()}%`;
    }

    try {
      setIsLoadingSubmit(true);
      const emailValue = getValues("clientEmailAddress");
      const generatePayload = {
        contactGroup: selectedContactName,
        clientName: values.clientName,
        ...(!isClientEmailDisabled && emailValue
          ? { clientEmail: emailValue }
          : {}),
        scenarioName: values.scenarioName,
        referred: wasReferred ? "Yes" : "No",
        newClient: toggleSwitch.isNewClient ? "Yes" : "No",
        equityContribution: values.equityContribution
          ? parseFloat(
              values.equityContribution.toString().replace(/[^0-9.]/g, "")
            )
          : 0,
        singleOrMFJ: values.singleOrMFJ || "Single",
        useAdjustedGrossIncome: toggleSwitch.isUseAdjustedGrossIncome
          ? "On"
          : "Off",
        adjustedGrossIncome: values.adjustedGrossIncome
          ? parseFloat(
              values.adjustedGrossIncome.toString().replace(/[^0-9.]/g, "")
            )
          : 0,
        lossLimitation: toggleSwitch.isLossLimitation ? "On" : "Off",
        federalRevenueSpread: toggleSwitch.isFederalRevenueSpread
          ? "On"
          : "Off",
        monthPlacedInService: values.monthlyPlacedInService
          ? parseInt(values.monthlyPlacedInService)
          : 0,
        federalBonusDepreciation: toggleSwitch.isFederalBonusDepreciation
          ? "On"
          : "Off",
        federalBonusDepreciationPercent:
          values.federalBonusDepreciationPercentage
            ? parseFloat(
                values.federalBonusDepreciationPercentage
                  .toString()
                  .replace("%", "")
              ) / 100
            : 0,
        state: stateName?.label || "",
        itcPercent: values.ITCPercentage
          ? parseFloat(values.ITCPercentage.toString().replace("%", "")) / 100
          : 0,
        devFeeMarkup: values.developerDevFee
          ? parseFloat(values.developerDevFee.toString().replace("%", ""))
          : 0,
        affiliateMarkupPercent: partnerMarkup
          ? parseFloat(partnerMarkup.toString().replace("%", "")) / 100
          : 0,
        federalMarginalTaxRate: values.federalMarginalTaxRate
          ? parseFloat(
              values.federalMarginalTaxRate.toString().replace("%", "")
            ) / 100
          : 0,
        portionPV: values.portionPv
          ? parseFloat(values.portionPv.toString().replace("%", "")) / 100
          : 0,
        stateTaxRate: values.stateTaxRate
          ? parseFloat(values.stateTaxRate.toString().replace("%", "")) / 100
          : 0,
        stateTaxRateYr: values.stateTaxRateYr7to20
          ? parseFloat(values.stateTaxRateYr7to20.toString().replace("%", "")) /
            100
          : 0,
        statePTEPaymentPercent: values.statePTEPayment
          ? parseFloat(values.statePTEPayment.toString().replace("%", "")) / 100
          : 0,
        stateBonusDepreciation: values.stateBonusDepreciation ? "Yes" : "No",
        stateDepreciationFED: values.stateBonusDepreciationAsFED ? "Yes" : "No",
        stateRevenueSpread: values.stateRevenueSpread ? "Yes" : "No",
        federalRevenueRecognitionYear:
          values.federalRevenueRecognitionYear || 1,
        ...(values.year2DeferralPossible === "YES"
          ? {
              stateRevenueRecognitionYear:
                values.stateRevenueRecognitionYear || 1,
            }
          : {}),
        inceptionDevFee: values.inceptionDevFee
          ? parseFloat(values.inceptionDevFee.toString().replace("%", ""))
          : 0,
        driveId: toggleSwitch.isAmpVantageActive
          ? config.REACT_APP_AMP_VANTAGE_FOLDER_ID || ""
          : defaultValues?.data[0]?.folderId || "",
        // proformaUrlId: proformaUrlId && proformaUrlId.trim() !== ""
        //   ? proformaUrlId
        //   : (urlId || ""),
        externalUserFolderId: isReallyExternal
          ? profromaUserDetails?.folderId || ""
          : contactGroupData?.data?.find(
              (item) => item.contactGroupName === selectedContactName
            )?.folderId || "",
        // isIndividualIdZoho: isIndividualIdZoho,
        designation: profromaUserDetails?.designation || "Internal",
        fullProforma: isAdmin ? toggleSwitch.isFullProforma : false,
      };

      let res = await generateProforma(generatePayload).unwrap();
      if (res?.error) {
        showNotification(
          "error",
          `We are sorry, your Pro Forma was not able to generate. Error: ${res.error}`
        );
      } else if (res?.folderUrl) {
        if (!isClientEmailDisabled) {
          await sendEmail(res.folderUrl);
        }
        setDriveFolderId(res);
        setProformaId(res.proformaId || "");
      }
    } catch (error) {
      console.log("Login error:", error);
      setErrMsg("An error occurred during login.");
    } finally {
      setIsLoadingSubmit(false);
      setProformaUrlId("");
      setWithoutEmailModal(false);
    }
  };

  // console.log(`watch("clientName")`, watch("ITCPercentage"));

  const sendEmail = async (proformaUrl: string | null) => {
    try {
      if (!proformaUrl) throw new Error("Proforma url is null");
      const body: ISendProformaEmail = {
        contactGroupId: isReallyExternal
          ? profromaUserDetails?.contactGroupId || ""
          : contactGroupOptions?.filter(
              (item) => item.label === selectedContactName
            )![0]?.value || "",
        clientName: watch("clientName"),
        proformaUrl: proformaUrl,
      };
      const response = await sendProformaEmail(body).unwrap();
      if (!response.success)
        throw new Error("Failed to send email to contact groups");
    } catch (err) {
      console.log("err", err);
    }
  };

  const onChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | { target: { name: string; value: string | boolean } }
  ) => {
    const { name, value } = e.target;
    if (name === "year1DepreciationUsed") {
      let rawValue = value
        .toString()
        .replace("$0", "")
        .replace(/[^\d.]/g, "");
      let formattedValue = "";
      if (rawValue === "") {
        formattedValue = `$0${rawValue}`;
      } else {
        formattedValue = `-$${rawValue}`;
      }
      setValue(name, formattedValue);
    } else if (
      name === "stateTaxRate" ||
      name === "stateTaxRateYr7to20" ||
      name === "statePTEPayment" ||
      name === "federalBonusDepreciationPercentage" ||
      name === "federalMarginalTaxRate" ||
      name === "portionPv" ||
      name === "inceptionDevFee" ||
      name === "ITCPercentage" ||
      name === "developerDevFee"
    ) {
      let rawValue = value
        .toString()
        .replace("%", "")
        .replace(/[^\d.]/g, "");
      // const formattedValue = `${rawValue}%`;
      let formattedValue = "";
      if (rawValue === "") {
        formattedValue = `${rawValue}0%`;
      } else {
        if (rawValue[0] === "0") rawValue = rawValue.substring(1);
        formattedValue = `${rawValue}%`;
      }
      // console.log("111111");
      setValue(name, formattedValue);
    } else {
      setValue(name as keyof ProformaForm, value);
    }
    setErrMsg("");
  };
  const setInputFieldsOnStateChange = (selectedOption: StateOption) => {
    if (!states?.data.length) {
      return;
    }
    const targetState = states?.data.filter((state) => {
      return state._id === selectedOption.value;
    });
    const { year2DeferralPossible, year2DeferralDefault } = targetState[0];
    setValue("year2DeferralPossible", year2DeferralPossible);
    setValue("federalRevenueRecognitionYear", 1);
    if (year2DeferralPossible === "YES") {
      setValue(
        "stateRevenueRecognitionYear",
        year2DeferralDefault === "YES" ? 2 : 1
      );
    } else {
      setValue("stateRevenueRecognitionYear", undefined);
    }
    // isExternal &&
    setBaseLineValues(targetState);
    setStateTaxRatePercent(targetState![0].stateTaxRatePercent);
    setValue(
      "stateTaxRate",
      `${targetState![0].stateTaxRatePercent.toString()}%`
    );
    setValue(
      "stateTaxRateYr7to20",
      `${targetState![0].stateTaxRatePercentYrs7_20.toString()}%`
    );
    setValue(
      "statePTEPayment",
      `${targetState![0].statePTEPaymentPercent.toString()}%`
    );
    setValue(
      "stateBonusDepreciation",
      targetState![0].stateBonusDepreciation === "NO" ? false : true
    );
    setValue(
      "stateBonusDepreciationAsFED",
      targetState![0].stateDepreciation === "NO" ? false : true
    );
    setValue(
      "stateRevenueSpread",
      targetState![0].stateRevenueSpread === "NO" ? false : true
    );
  };

  const resetForm = () => {
    const currentDate = new Date();
    // console.log("2222");
    reset({
      year1DepreciationUsed: dataToPrefill[13][1] || "$0", // need assumption api to prefill this value
      stateSelector: {
        label: "California",
        value: "California",
      },
      federalMarginalTaxRate: "37%",
      portionPv: "30%",
      monthlyPlacedInService:
        currentDate.getMonth() + 1 === 12
          ? "12"
          : String(currentDate.getMonth() + 2),
      federalBonusDepreciationPercentage: `${baselineModalData?.data?.federalBonusDepreciation}%`,
      developerDevFee: isReallyExternal
        ? `${profromaUserDetails.developerDevFee}%`
        : `${baselineModalData?.data?.developerDevFee}%`,
      ITCPercentage: isReallyExternal
        ? `${profromaUserDetails?.itcPercent}%`
        : toggleSwitch.isAmpVantageActive
        ? `${baselineModalData?.data.ampVantageItcPercent}%`
        : `${baselineModalData?.data?.itcPercent}%`,
      contactGroup: contactGroupOptions?.filter(
        (item) => item.label === selectedContactName
      ),
      singleOrMFJ: "Single",
    });
    setInputFieldsOnStateChange({
      label: "California",
      value: "65d829be7389fe6c0ee54682",
    });
    let newObj = { ...toggleSwitch };
    newObj.isLossLimitation = baselineModalData?.data?.lossLimitation!;
    newObj.isFederalRevenueSpread =
      baselineModalData?.data?.federalRevenueSpread!;
    newObj.isFederalBonusDepreciation =
      dataToPrefill[32][1] === "Yes" ? true : false;
    newObj.isNewClient = false;
    newObj.isFullProforma = true;
    setToggleSwitch(newObj);
    const targetState = states?.data.filter((state) => {
      return state.state === "California";
    });
    setStateTaxRatePercent(targetState![0].stateTaxRatePercent);
    isExternal && setBaseLineValues(targetState);
  };

  const setBaseLineValues = (targetState: any) => {
    const baseData = baselineModalData?.data;
    const isAmp = selectedContactName === "AmpVantage Energy";

    // const isNotExternal = [
    //   "AmpVantage Energy",
    //   "Inception Financial",
    // ].includes(selectedContactName)

    // setIsExternal(
    //   !["AmpVantage Energy", "Inception Financial"].includes(
    //     selectedContactName
    //   )
    // );

    const selectedContactGroup = contactGroupData?.data.filter(
      (item) => item.contactGroupName === selectedContactName
    );
    const taxRate = targetState![0].stateTaxRatePercent;
    //compare selected state tax rate percent with proforma backend values
    //Low Scenario
    if (
      taxRate >= baseData?.minStateTaxRateL! &&
      taxRate <= baseData?.maxStateTaxRateL!
    ) {
      !isAmp &&
        setValue(
          "inceptionDevFee",
          `${baseData?.inceptionDevFeeL.toString()}%`
        );
      // for fake external users use selected contact group's lowScenario
      setValue("partnerMarkup", selectedContactGroup![0].lowScenerio + "%");
    }
    //Low - Mid Scenario
    else if (
      taxRate > baseData?.minStateTaxRateLM! &&
      taxRate <= baseData?.maxStateTaxRateLM!
    ) {
      !isAmp &&
        setValue(
          "inceptionDevFee",
          `${baseData?.inceptionDevFeeLM.toString()}%`
        );

      setValue("partnerMarkup", selectedContactGroup![0].lowMidScenerio + "%");
    }
    // High - Mid Scenario
    else if (
      taxRate > baseData?.minStateTaxRateHM! &&
      taxRate <= baseData?.maxStateTaxRateHM!
    ) {
      !isAmp &&
        setValue(
          "inceptionDevFee",
          `${baseData?.inceptionDevFeeHM.toString()}%`
        );

      setValue("partnerMarkup", selectedContactGroup![0].highMidScenerio + "%");
    }
    // High Scenario
    else {
      !isAmp &&
        setValue(
          "inceptionDevFee",
          `${baseData?.inceptionDevFeeH.toString()}%`
        );

      setValue("partnerMarkup", selectedContactGroup![0].highScenerio + "%");
    }

    // update ITC %

    const itc = isReallyExternal
      ? `${profromaUserDetails?.itcPercent}`
      : toggleSwitch.isAmpVantageActive
      ? `${baseData?.ampVantageItcPercent}`
      : `${baseData?.itcPercent}`;
    setValue("ITCPercentage", `${itc}%`);
    setValue(
      "federalBonusDepreciationPercentage",
      `${baseData?.federalBonusDepreciation!}%`
    );

    const developerDevFee = isReallyExternal
      ? `${profromaUserDetails.developerDevFee}`
      : `${baseData?.developerDevFee}`;
    setValue("developerDevFee", `${developerDevFee}%`);

    let newObj = { ...toggleSwitch };
    newObj.isLossLimitation = baseData?.lossLimitation!;
    newObj.isFederalRevenueSpread = [
      "AmpVantage Energy",
      "Inception Financial",
    ].includes(selectedContactName)
      ? newObj.isAmpVantageActive
      : baseData?.federalRevenueSpread!;
    setToggleSwitch(newObj);

    // const partnerMarkupValue = getValues("partnerMarkup");
    const allowedTwentyPercent =
      selectedContactGroup![0]?.allowTwentyPercentAffiliate;

    // check if the value is greater that 15
    const contactGroup = selectedContactGroup?.[0];
    const isGreaterThanFifteen =
      (contactGroup &&
        (Number(contactGroup?.lowScenerio) > 15 ||
          Number(contactGroup?.lowMidScenerio) > 15 ||
          Number(contactGroup?.highMidScenerio) > 15 ||
          Number(contactGroup?.highScenerio) > 15)) ??
      false;

    const isGreaterThanTwenty =
      (contactGroup &&
        (Number(contactGroup?.lowScenerio) > 20 ||
          Number(contactGroup?.lowMidScenerio) > 20 ||
          Number(contactGroup?.highMidScenerio) > 20 ||
          Number(contactGroup?.highScenerio) > 20)) ??
      false;
    updatePartnerMarkupOptions(
      !!allowedTwentyPercent,
      isGreaterThanFifteen,
      isGreaterThanTwenty
    );
  };
  const setBaseLineValuesWithoutPartnerMarkup = (targetState: any) => {
    const baseData = baselineModalData?.data;
    const selectedContactGroup = contactGroupData?.data.filter(
      (item) => item.contactGroupName === selectedContactName
    );
    const taxRate = targetState![0].stateTaxRatePercent;
    if (
      taxRate >= baseData?.minStateTaxRateL! &&
      taxRate <= baseData?.maxStateTaxRateL!
    ) {
      setValue("inceptionDevFee", `${baseData?.inceptionDevFeeL.toString()}%`);
    } else if (
      taxRate > baseData?.minStateTaxRateLM! &&
      taxRate <= baseData?.maxStateTaxRateLM!
    ) {
      setValue("inceptionDevFee", `${baseData?.inceptionDevFeeLM.toString()}%`);
    } else if (
      taxRate > baseData?.minStateTaxRateHM! &&
      taxRate <= baseData?.maxStateTaxRateHM!
    ) {
      setValue("inceptionDevFee", `${baseData?.inceptionDevFeeHM.toString()}%`);
    } else {
      setValue("inceptionDevFee", `${baseData?.inceptionDevFeeH.toString()}%`);
    }
  };

  useEffect(() => {
    getStates();
  }, [states]);

  useEffect(() => {
    if (assumptionInput?.data) {
      setDataToPrefill(assumptionInput.data);
    }
  }, [assumptionInput]);
  useEffect(() => {
    if (dataToPrefill.length > 0 && stateOptions) {
      resetForm();
    }
  }, [dataToPrefill, stateOptions, isReallyExternal, isExternal]);

  const handleLogout = () => {
    tracker.proforma.logout(profromaUserDetails.name);
    localStorage.clear();
    dispatch(proformaApi.util.resetApiState()); // to remove the caching of the api on logout
    dispatch(resetProformaUser());
    navigate("/proforma");
  };

  useEffect(() => {
    const contactOptions = () => {
      const data = contactGroupData?.data.map((item) => {
        return {
          label: item.contactGroupName,
          value: item._id!,
        };
      });
      if (!isReallyExternal) {
        setContactGroupOptions(data);
      }
      if (isReallyExternal) {
        const externalData = contactGroupData?.data.find(
          (item) =>
            item.contactGroupName === profromaUserDetails.contactGroupName
        );

        if (externalData) {
          const additionalGroups: IContactGroup[] =
            contactGroupData?.data.filter((item) =>
              externalData.additionalContactGroups?.includes(
                item.contactGroupName
              )
            ) ?? [];

          // Include the main group itself
          const combinedGroups = [externalData, ...additionalGroups];

          // Map to label/value format
          const formattedOptions = combinedGroups.map((item) => ({
            label: item.contactGroupName,
            value: item._id!,
          }));

          if (externalData?.additionalContactGroups?.length == 0) {
            setSelectedContactName(externalData?.contactGroupName);
            setIsContactGroupDisabled(true);
          }
          setContactGroupOptions(formattedOptions);
        }
      }
    };
    contactGroupData && contactOptions();
  }, [contactGroupData]);

  const handleContactGroupChange = (data: StateOption) => {
    const selectedContactGroup = contactGroupData?.data.filter(
      (item) => item.contactGroupName === data.label
    );
    console.log("selectedContactGroup", selectedContactGroup);
    setBaseLineName(
      selectedContactGroup![0]?.proformaModel?.modelName ?? "Baseline Model"
    );
    if (data.label === "Inception Financial") {
      let newObj = { ...toggleSwitch, isAmpVantageActive: false };
      newObj.isLossLimitation = baselineModalData?.data?.lossLimitation!;
      newObj.isFederalRevenueSpread = newObj.isAmpVantageActive;
      // console.log("33333");

      setValue("ITCPercentage", `${baselineModalData?.data?.itcPercent!}%`);
      setValue(
        "inceptionDevFee",
        `${defaultValues?.data?.[0]?.inceptionDevFee}%`
      );
      setToggleSwitch(newObj);
      setIsExternal(false);
    } else if (data.label === "AmpVantage Energy") {
      let newObj = { ...toggleSwitch, isAmpVantageActive: true };
      newObj.isLossLimitation = !newObj.isAmpVantageActive;
      newObj.isFederalRevenueSpread = newObj.isAmpVantageActive;
      // console.log("44444");
      setValue(
        "ITCPercentage",
        `${baselineModalData?.data?.ampVantageItcPercent!}%`
      );
      setValue("inceptionDevFee", `27.5%`);
      setToggleSwitch(newObj);
      setIsExternal(false);
    } else {
      setIsExternal(true);
      let newObj = { ...toggleSwitch, isAmpVantageActive: false };
      newObj.isLossLimitation = baselineModalData?.data?.lossLimitation!;
      newObj.isFederalRevenueSpread =
        baselineModalData?.data?.federalRevenueSpread!;
      setToggleSwitch(newObj);
      const selectedContactGroup = contactGroupData?.data.filter(
        (item) => item._id === data.value
      );
      const baseData = baselineModalData?.data;
      const taxRate = stateTaxRatePercent || 0;
      if (
        taxRate >= baseData?.minStateTaxRateL! &&
        taxRate <= baseData?.maxStateTaxRateL!
      ) {
        setValue("partnerMarkup", selectedContactGroup![0].lowScenerio + "%");
      } else if (
        taxRate > baseData?.minStateTaxRateLM! &&
        taxRate <= baseData?.maxStateTaxRateLM!
      ) {
        setValue(
          "partnerMarkup",
          selectedContactGroup![0].lowMidScenerio + "%"
        );
      } else if (
        taxRate > baseData?.minStateTaxRateHM! &&
        taxRate <= baseData?.maxStateTaxRateHM!
      ) {
        setValue(
          "partnerMarkup",
          selectedContactGroup![0].highMidScenerio + "%"
        );
      } else {
        setValue("partnerMarkup", selectedContactGroup![0].highScenerio + "%");
      }
    }

    const allowedTwentyPercent =
      selectedContactGroup![0]?.allowTwentyPercentAffiliate;

    // check if the value is greater that 15
    const contactGroup = selectedContactGroup?.[0];
    const isGreaterThanFifteen =
      (contactGroup &&
        (Number(contactGroup?.lowScenerio) > 15 ||
          Number(contactGroup?.lowMidScenerio) > 15 ||
          Number(contactGroup?.highMidScenerio) > 15 ||
          Number(contactGroup?.highScenerio) > 15)) ??
      false;

    const isGreaterThanTwenty =
      (contactGroup &&
        (Number(contactGroup?.lowScenerio) > 20 ||
          Number(contactGroup?.lowMidScenerio) > 20 ||
          Number(contactGroup?.highMidScenerio) > 20 ||
          Number(contactGroup?.highScenerio) > 20)) ??
      false;

    updatePartnerMarkupOptions(
      !!allowedTwentyPercent,
      isGreaterThanFifteen,
      isGreaterThanTwenty
    );
  };
  const showRevenueSpread = useMemo(() => {
    if (selectedContactName === "Inception Financial") return true;
    const currentGroup = contactGroupData?.data?.find(
      (item) => item.contactGroupName === selectedContactName
    );
    return currentGroup?.showRevenueSpread ?? false;
  }, [selectedContactName, contactGroupData]);

  useEffect(() => {
    if (baseLineName) {
      refetch();
    }
  }, [baseLineName]);
  useEffect(() => {
    if (baselineModalData?.data && states?.data?.length) {
      // get the currently selected state from your form
      const selectedState = getValues("stateSelector")?.value;

      let targetState;

      if (selectedState) {
        // match by _id or state name depending on your StateOption structure
        targetState = states.data.filter(
          (state) =>
            state._id === selectedState || state.state === selectedState
        );
      } else {
        // fallback to default California if nothing selected
        targetState = states.data.filter(
          (state) => state.state === "California"
        );
      }

      if (targetState?.length > 0) {
        setBaseLineValues(targetState);
      }
      const stateDetails = watch("contactGroup");
      console.log(`stateDetails`, stateDetails);
      // handleContactGroupChange(stateDetails[0]);
    }
  }, [baselineModalData]);

  useEffect(() => {
    if (contactGroupData?.data?.length && selectedContactName) {
      const initialGroup = contactGroupData.data.find(
        (item) => item.contactGroupName === selectedContactName
      );
      setBaseLineName(
        initialGroup?.proformaModel?.modelName ?? "Baseline Model"
      );
    }
  }, [contactGroupData]);
  useEffect(() => {
    if (
      defaultValues?.data?.[0] &&
      profromaUserDetails?.designation === "Internal"
    ) {
      reset({ inceptionDevFee: `${defaultValues.data[0].inceptionDevFee}%` });
    }
  }, [defaultValues]);

  useEffect(() => {
    tracker.proforma.proformaFormView(profromaUserDetails?.name);
  }, []);
  useEffect(() => {
    if (isDirty && !hasStarted.current) {
      hasStarted.current = true;
      tracker.proforma.formStarted();
    }
  }, [isDirty]);

  if (driveFolderId) {
    return (
      <ProformaResponse
        proformaId={proformaId}
        contactEmail={contactEmail}
        folderUrl={driveFolderId.folderUrl}
        onReset={() => setDriveFolderId(null)}
        resetForm={resetForm}
        handleClientEmail={handleClientEmailClear}
      />
    );
  }

  const formatNumber = (value: string) => {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const showLoader =
    assumptionInputLoader ||
    loading ||
    defaultValuesLoader ||
    baselineModalLoader ||
    contactGroupLoader;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <img src={img} alt="" className={`${styles.image}`} />
        </div>
        {/* {profromaUserDetails?.designation !== "External" && (
          <div className={styles.switchBox}>
            <span className={styles.span}>AmpVantage Energy</span>
            <CustomSwitch
              isActive={toggleSwitch.isAmpVantageActive}
              names="isAmpVantageActive"
              handleClick={handleClick}
            />
          </div>
        )} */}
        <div className={styles.buttonBox}>
          <div
            className={`relative flex items-center justify-center bg-[#3B755E]  rounded ${styles.greetingMessage}`}
          >
            <p className="text-center  align-middle">
              Hello, {profromaUserDetails?.name}
            </p>
          </div>

          {/* button */}
          <button
            onClick={() => {
              tracker.proforma.finalizeProforma(profromaUserDetails.name);
              setIsExistingProformaModalOpen(true);
            }}
            className={`relative  bg-[#3B755E] cursor-pointer rounded ${styles.proformaButton}`}
          >
            FINALIZE PROFORMA
          </button>
          {/* button */}
          <button
            onClick={handleLogout}
            className={`relative  bg-[#3B755E] cursor-pointer rounded ${styles.logoutButton}`}
          >
            LOGOUT
          </button>
        </div>
      </div>
      {showLoader ? (
        <Loader isLoading={showLoader} size={48} />
      ) : (
        <form
          className={styles.content}
          onSubmit={handleSubmit(handleEmailDisable, onError)}
          noValidate
        >
          {isLoadingSubmit ? (
            <>
              <h1 className={`font-bold text-center ${styles.formHeader}`}>
                Generating your proforma. We appreciate your patience.
              </h1>
              <div className={styles.loaderBox}>
                <Loader isLoading={isLoadingSubmit} />
              </div>
            </>
          ) : (
            <>
              <h1 className={`font-bold text-center ${styles.formHeader}`}>
                Fill Out Your Information
              </h1>
              {isAdmin && (
                <div className={styles.formLineFull}>
                  <span className={styles.inputLabel}>Full Proforma</span>
                  <CustomSwitch
                    isActive={toggleSwitch.isFullProforma}
                    names="isFullProforma"
                    handleClick={handleClick}
                    displayType="yesno"
                  />
                </div>
              )}
              <div className={`${styles.formLineFull}`}>
                <label htmlFor="contactGroup" className={styles.inputLabel}>
                  Contact Group:
                </label>
                <Controller
                  name="contactGroup"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      id="contactGroup"
                      isLoading={loading}
                      isDisabled={isContactGroupDisabled}
                      className={`!p-0 ${styles.controller}`}
                      options={contactGroupOptions}
                      value={field.value}
                      onChange={(selectedOption) => {
                        if (
                          !field.value ||
                          field.value.value !== selectedOption.value
                        ) {
                          handleContactGroupChange(selectedOption);
                        }
                        setSelectedContactName(selectedOption.label);
                        field.onChange(selectedOption);
                      }}
                    />
                  )}
                />
              </div>
              <div className={styles.formLineFull}>
                <p className={styles.inputLabel}>
                  Client Email Address{!isClientEmailDisabled && "*"}
                  <span className={styles.tooltipWrapper}>
                    <FaRegQuestionCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>
                      Email should match Contact Record
                    </span>
                  </span>
                </p>

                <div className={styles.inputContainer}>
                  <input
                    type="email"
                    id="clientEmailAddress"
                    className={styles.inputBox}
                    disabled={isClientEmailDisabled}
                    {...register("clientEmailAddress")}
                  />
                  {errors.clientEmailAddress && (
                    <p className={styles.error}>
                      {errors.clientEmailAddress.message}
                    </p>
                  )}
                </div>
              </div>

              <div className={styles.toggleContainer}>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={isClientEmailDisabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsClientEmailDisabled(checked);
                      setValue("isClientEmailDisabled", checked);
                      if (checked || isClientEmailDisabled) {
                        setValue("clientEmailAddress", "");
                        // Optionally clear validation error
                        setContactEmail("");
                        clearErrors("clientEmailAddress");
                      }
                    }}
                  />
                  Don't require Client Email
                </label>
              </div>

              <div className={styles.formLineFull}>
                <p className={styles.inputLabel}>Client Name*</p>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    id="clientName"
                    className={styles.inputBox}
                    {...register("clientName")}
                  />
                  {errors.clientName && (
                    <p className={styles.error}>{errors.clientName.message}</p>
                  )}
                </div>
              </div>

              {!isExternal && selectedContactName === "Inception Financial" && (
                <div className={`${styles.formLineFull}`}>
                  <span className={styles.inputLabel}>
                    Was this person referred?
                  </span>
                  <CustomSwitch
                    isActive={wasReferred}
                    names="wasReferred"
                    handleClick={() => setWasReferred((prev) => !prev)}
                    displayType="yesno" // This one shows YES/NO
                  />
                </div>
              )}

              <div className={styles.formLineFull}>
                <p className={styles.inputLabel}>Scenario Name*</p>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    id="scenarioName"
                    className={styles.inputBox}
                    {...register("scenarioName")}
                  />
                  {errors.scenarioName && (
                    <p className={styles.error}>
                      {errors.scenarioName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className={`${styles.formLineFull}`}>
                <span className={styles.inputLabel}>Is this a New Client?</span>
                <CustomSwitch
                  isActive={toggleSwitch.isNewClient}
                  names="isNewClient"
                  handleClick={handleClick}
                  displayType="yesno"
                />
              </div>

              <div className={`${styles.formLineFull}`}>
                <label htmlFor="singleOrMFJ" className={styles.inputLabel}>
                  Tax Filing Status
                </label>
                <div className={styles.inputContainer}>
                  <Controller
                    name="singleOrMFJ"
                    control={control}
                    render={({ field }) => (
                      <select
                        {...field}
                        id="singleOrMFJ"
                        className={styles.inputBox}
                      >
                        <option value="Single">Single</option>
                        <option value="MFJ">MFJ</option>
                      </select>
                    )}
                  />
                  {errors.singleOrMFJ && (
                    <p className={styles.error}>{errors.singleOrMFJ.message}</p>
                  )}
                </div>
              </div>

              <div className={styles.formLineFull}>
                <p className={styles.inputLabel}>Equity Contribution</p>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    id="equityContribution"
                    className={styles.inputBox}
                    disabled={
                      toggleSwitch.isUseAvailableTaxCredit ||
                      toggleSwitch.isUseAdjustedGrossIncome
                    }
                    {...register("equityContribution", {
                      onChange: (e) => {
                        const rawValue = e.target.value.replace(/,/g, "");
                        const formattedValue = formatNumber(rawValue);
                        e.target.value = formattedValue;
                      },
                    })}
                  />
                  {errors.equityContribution && (
                    <p className={styles.error}>
                      {errors.equityContribution.message}
                    </p>
                  )}
                </div>
              </div>
              <div
                className={`${
                  toggleSwitch.isAmpVantageActive ||
                  !toggleSwitch.isUseAvailableTaxCredit
                    ? "!hidden"
                    : ""
                } ${styles.formLineFull}`}
              >
                <p className={styles.inputLabel}>ITC Amount</p>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    id="ITCAmount"
                    className={styles.inputBox}
                    {...register("ITCAmount")}
                  />
                  {errors.ITCAmount && (
                    <p className={styles.error}>{errors.ITCAmount.message}</p>
                  )}
                </div>
              </div>
              <div className={`${styles.formLineFull}`}>
                <span className={styles.inputLabel}>
                  Use Adjusted Gross Income*
                </span>
                <CustomSwitch
                  isActive={toggleSwitch.isUseAdjustedGrossIncome}
                  names="isUseAdjustedGrossIncome"
                  handleClick={handleClick}
                />
              </div>
              <div
                className={`${
                  !toggleSwitch.isUseAdjustedGrossIncome ? "!hidden" : ""
                } ${styles.formLineFull}`}
              >
                <p className={styles.inputLabel}>Adjusted Gross Income</p>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    id="adjustedGrossIncome"
                    className={styles.inputBox}
                    {...register("adjustedGrossIncome", {
                      onChange: (e) => {
                        const rawValue = e.target.value.replace(/,/g, "");
                        const formattedValue = formatNumber(rawValue);
                        e.target.value = formattedValue;
                      },
                    })}
                  />
                  {errors.adjustedGrossIncome && (
                    <p className={styles.error}>
                      {errors.adjustedGrossIncome.message}
                    </p>
                  )}
                </div>
              </div>
              <div className={clsx(styles.formLineFull)}>
                <span className={styles.inputLabel}>Loss Limitation*</span>
                <CustomSwitch
                  isActive={toggleSwitch.isLossLimitation}
                  names="isLossLimitation"
                  handleClick={handleClick}
                />
              </div>
              {showRevenueSpread && (
                <>
                  <div className={`${styles.formLineFull}`}>
                    <span className={styles.inputLabel}>
                      Federal Revenue Spread*
                    </span>
                    <CustomSwitch
                      isActive={toggleSwitch.isFederalRevenueSpread}
                      names="isFederalRevenueSpread"
                      handleClick={handleClick}
                    />
                  </div>
                  <div
                    className={`${
                      !toggleSwitch.isFederalRevenueSpread ? "!hidden" : ""
                    } ${styles.formLineFull}`}
                  >
                    <label
                      htmlFor="monthlyPlacedInService"
                      className={styles.inputLabel}
                    >
                      Month Placed In Service:
                    </label>
                    <Controller
                      name="monthlyPlacedInService"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          id="monthlyPlacedInService"
                          className={styles.controller}
                        >
                          {months.map((month, index) => (
                            <option key={index} value={month}>
                              {month}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {errors.monthlyPlacedInService && (
                      <p>{errors.monthlyPlacedInService.message}</p>
                    )}
                  </div>
                  <div className={`${styles.formLineFull}`}>
                    <span className={styles.inputLabel}>
                      Federal Bonus Depreciation*
                    </span>
                    <CustomSwitch
                      isActive={toggleSwitch.isFederalBonusDepreciation}
                      names="isFederalBonusDepreciation"
                      handleClick={handleClick}
                    />
                  </div>
                  <div
                    className={`${styles.formLineFull}
                     
                    `}
                  >
                    <p className={styles.inputLabel}>
                      Federal Bonus Depreciation %
                    </p>
                    <div className={styles.inputContainer}>
                      <input
                        type="text"
                        id="federalBonusDepreciationPercentage"
                        className={`${styles.inputBox} ${
                          !toggleSwitch.isFederalBonusDepreciation
                            ? "text-gray-700"
                            : ""
                        }`}
                        {...register("federalBonusDepreciationPercentage")}
                        onChange={onChange}
                        disabled={!toggleSwitch.isFederalBonusDepreciation}
                      />
                      {errors.federalBonusDepreciationPercentage && (
                        <p className={styles.error}>
                          {errors.federalBonusDepreciationPercentage.message}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className={`${styles.formLineFull}`}>
                <label htmlFor="stateSelector" className={styles.inputLabel}>
                  Select a State:
                </label>
                <Controller
                  name="stateSelector"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      id="stateSelector"
                      isLoading={loading}
                      className={`!p-0 ${styles.controller}`}
                      options={stateOptions}
                      // options={stateOptions as OptionsOrGroups<string, GroupBase<string>>}
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption);
                        setInputFieldsOnStateChange(selectedOption);
                      }}
                    />
                  )}
                />
                {/* {errors.stateSelector && <p>{errors.stateSelector.message}</p>} */}
              </div>
              <div className={`${styles.formLineFull}`}>
                <p className={styles.inputLabel}>State Tax Rate</p>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    id="stateTaxRate"
                    className={styles.inputBox}
                    {...register("stateTaxRate")}
                    onChange={onChange}
                  />
                  {errors.stateTaxRate && (
                    <p className={styles.error}>
                      {errors.stateTaxRate.message}
                    </p>
                  )}
                </div>
              </div>
              <div className={`${styles.formLineFull}`}>
                <p className={styles.inputLabel}>State Tax Rate Yr 7-20</p>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    id="stateTaxRateYr7to20"
                    className={styles.inputBox}
                    {...register("stateTaxRateYr7to20")}
                    onChange={onChange}
                  />
                  {errors.stateTaxRateYr7to20 && (
                    <p className={styles.error}>
                      {errors.stateTaxRateYr7to20.message}
                    </p>
                  )}
                </div>
              </div>
              {!isExternal && (
                <>
                  <div className={`${styles.formLineFull}`}>
                    <p className={styles.inputLabel}>State PTE Payment %</p>
                    <div className={styles.inputContainer}>
                      <input
                        type="text"
                        id="statePTEPayment"
                        className={styles.inputBox}
                        {...register("statePTEPayment")}
                        onChange={onChange}
                      />
                      {errors.statePTEPayment && (
                        <p className={styles.error}>
                          {errors.statePTEPayment.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={`${styles.formLineFull}`}>
                    <label
                      htmlFor="stateBonusDepreciation"
                      className={styles.inputLabel}
                    >
                      State Bonus Depreciation
                    </label>
                    <Controller
                      name="stateBonusDepreciation"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          id="stateBonusDepreciation"
                          className={styles.controller}
                          value={field.value === true ? "true" : "false"}
                          onChange={(e) => {
                            const booleanValue = e.target.value === "true";
                            onChange({
                              target: { name: field.name, value: booleanValue },
                            });
                            field.onChange(booleanValue);
                          }}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      )}
                    />
                    {errors.stateBonusDepreciation && (
                      <p>{errors.stateBonusDepreciation.message}</p>
                    )}
                  </div>
                  <div className={`${styles.formLineFull}`}>
                    <label
                      htmlFor="stateBonusDepreciationAsFED"
                      className={styles.inputLabel}
                    >
                      State Depreciation Same As FED Basis
                    </label>
                    <Controller
                      name="stateBonusDepreciationAsFED"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          id="stateBonusDepreciationAsFED"
                          className={styles.controller}
                          value={field.value === true ? "true" : "false"}
                          onChange={(e) => {
                            const booleanValue = e.target.value === "true";
                            onChange({
                              target: { name: field.name, value: booleanValue },
                            });
                            field.onChange(booleanValue);
                          }}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      )}
                    />
                    {errors.stateBonusDepreciationAsFED && (
                      <p>{errors.stateBonusDepreciationAsFED.message}</p>
                    )}
                  </div>
                </>
              )}
              {showRevenueSpread && (
                <div className={`${styles.formLineFull}`}>
                  <label
                    htmlFor="stateRevenueSpread"
                    className={styles.inputLabel}
                  >
                    State Revenue Spread
                  </label>
                  <Controller
                    name="stateRevenueSpread"
                    control={control}
                    render={({ field }) => (
                      <select
                        {...field}
                        id="stateRevenueSpread"
                        className={styles.controller}
                        value={field.value === true ? "true" : "false"}
                        onChange={(e) => {
                          const booleanValue = e.target.value === "true";
                          onChange({
                            target: { name: field.name, value: booleanValue },
                          });
                          field.onChange(booleanValue);
                        }}
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    )}
                  />
                  {errors.stateRevenueSpread && (
                    <p>{errors.stateRevenueSpread.message}</p>
                  )}
                </div>
              )}
              {showFederalRevenueYearSelect && (
                <div className={styles.formLineFull}>
                  <label className={styles.inputLabel}>
                    Federal Revenue Recognition Year
                  </label>
                  <Controller
                    name="federalRevenueRecognitionYear"
                    control={control}
                    render={({ field }) => (
                      <select {...field} className={styles.controller}>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                      </select>
                    )}
                  />
                </div>
              )}
              {watch("year2DeferralPossible") === "YES" && (
                <div className={styles.formLineFull}>
                  <label className={styles.inputLabel}>
                    State Revenue Recognition Year
                  </label>
                  <Controller
                    name="stateRevenueRecognitionYear"
                    control={control}
                    render={({ field }) => (
                      <select {...field} className={styles.controller}>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                      </select>
                    )}
                  />
                </div>
              )}
              {!isExternal ? (
                <>
                  <div className={`${styles.formLineFull}`}>
                    <p className={styles.inputLabel}>ITC %</p>
                    <div className={styles.inputContainer}>
                      <input
                        type="text"
                        id="ITCPercentage"
                        className={styles.inputBox}
                        {...register("ITCPercentage")}
                        onChange={onChange}
                      />
                      {errors.ITCPercentage && (
                        <p className={styles.error}>
                          {errors.ITCPercentage.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={`${styles.formLineFull}`}>
                    <p className={styles.inputLabel}>Inception Dev Fee</p>
                    <div className={styles.inputContainer}>
                      <input
                        type="text"
                        id="inceptionDevFee"
                        className={styles.inputBox}
                        {...register("inceptionDevFee")}
                        onChange={onChange}
                      />
                      {errors.inceptionDevFee && (
                        <p className={styles.error}>
                          {errors.inceptionDevFee.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={`${styles.formLineFull}`}>
                    <p className={styles.inputLabel}>Developer Dev Fee</p>
                    <div className={styles.inputContainer}>
                      <input
                        type="text"
                        id="developerDevFee"
                        className={styles.inputBox}
                        {...register("developerDevFee")}
                        onChange={onChange}
                      />
                      {errors.developerDevFee && (
                        <p className={styles.error}>
                          {errors.developerDevFee.message}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className={`${styles.formLineFull}`}>
                  <p className={styles.inputLabel}>
                    {/* Partner markup */}
                    Affiliate Markup %
                    <span className={styles.tooltipWrapper}>
                      <FaRegQuestionCircle className={styles.tooltipIcon} />
                      <span className={styles.tooltipText}>Markup of EC</span>
                    </span>
                  </p>
                  <Controller
                    name="partnerMarkup"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        id="partnerMarkup"
                        className={`!p-0 ${styles.controller}`}
                        options={partnerMarkupOptions}
                        onChange={(selectedOption) => {
                          setValue(
                            "partnerMarkup",
                            selectedOption?.value || "0%"
                          );
                        }}
                        value={partnerMarkupOptions.find(
                          (option) => option.value === watch("partnerMarkup")
                        )}
                        menuPlacement="top"
                      />
                    )}
                  />
                  {errors.partnerMarkup && (
                    <p className={styles.error}>
                      {errors.partnerMarkup.message}
                    </p>
                  )}
                </div>
              )}
              <div className={`${styles.formLineFull}`}>
                <p className={styles.inputLabel}>Federal Marginal Tax Rate</p>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    id="federalMarginalTaxRate"
                    className={styles.inputBox}
                    {...register("federalMarginalTaxRate")}
                    onChange={onChange}
                  />
                  {errors.federalMarginalTaxRate && (
                    <p className={styles.error}>
                      {errors.federalMarginalTaxRate.message}
                    </p>
                  )}
                </div>
              </div>

              {!isExternal && (
                <div className={`${styles.formLineFull}`}>
                  <p className={styles.inputLabel}>Portion PV</p>
                  <div className={styles.inputContainer}>
                    <input
                      type="text"
                      id="portionPv"
                      className={styles.inputBox}
                      {...register("portionPv")}
                      onChange={onChange}
                    />
                    {errors.portionPv && (
                      <p className={styles.error}>{errors.portionPv.message}</p>
                    )}
                  </div>
                </div>
              )}

              <div className={`${styles.formLineFull} !justify-center`}>
                <button
                  type="submit"
                  className={`relative cursor-pointer rounded ${styles.submitButton}`}
                >
                  {isExistingProformaLoading ? (
                    <Loader isLoading={true} />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      )}
      <GlobalModal
        open={isExistingProformaModalOpen}
        setOpen={setIsExistingProformaModalOpen}
      >
        <ExistingProforma
          onProformaCheckResult={handleProformaCheckResult}
          setProformaId={setProformaId}
          setContactEmail={setContactEmail}
        />
      </GlobalModal>

      <GlobalModal
        open={isProformaLocatedModalOpen}
        setOpen={setIsProformaLocatedModalOpen}
      >
        {proformaMatchStatus !== null && (
          <ProformaLocated
            isMatch={proformaMatchStatus}
            proformaId={proformaId}
            contactEmail={contactEmail}
            message={proformaLocatedMessage}
            setIsProformaLocatedModalOpen={setIsProformaLocatedModalOpen}
            isAlreadyFinalized={isAlreadyFinalized}
          />
        )}
      </GlobalModal>
      <GlobalModal open={withoutEmailModal} setOpen={setWithoutEmailModal}>
        <WithoutValidEmailModal
          onSubmit={() => {
            setWithoutEmailModal(false);
            if (selectedContactName === "AmpVantage Energy") {
              tracker.proforma.ampProformaSubmit(profromaUserDetails?.name);
            } else {
              tracker.proforma.regularProformaSubmit(profromaUserDetails?.name);
            }
            if (latestFormValues.current) {
              onSubmit(latestFormValues.current);
            }
          }}
          onCancel={() => setWithoutEmailModal(false)}
        />
      </GlobalModal>
    </div>
  );
};

export default ProformaFormV2;
